/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */
import React from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import SiteEquipmentList from "./SiteEquipmentList";
import { SiteMetadata } from "../../lib/Types";

interface ConfigureDataModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (selectedMetrics: string[]) => void;
  site: SiteMetadata;
  assetTypeLabel: string;
  selectedMetricUuids: string[];
  onMetricsChange: (metrics: string[]) => void;
  isSaving?: boolean;
}

const ConfigureDataModal: React.FC<ConfigureDataModalProps> = ({
  open,
  onClose,
  onSave,
  site,
  assetTypeLabel,
  selectedMetricUuids,
  onMetricsChange,
  isSaving = false,
}) => {
  const handleSave = () => {
    onSave(selectedMetricUuids);
  };

  const handleClose = () => {
    if (!isSaving) onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "80%",
          maxWidth: 980,
          maxHeight: "90vh",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block", mb: 0.5 }}
          >
            Activity
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, flex: 1 }}>
              Configure {assetTypeLabel} Data
            </Typography>
          </Box>
        </Box>

        <Box sx={{ flex: 1, overflow: "auto", mx: -4, px: 0 }}>
          <SiteEquipmentList
            site={site}
            updateSelectedMetrics={onMetricsChange}
            selectedMetricUuids={selectedMetricUuids}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            pt: 3,
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color="secondary"
            onClick={handleSave}
            loading={isSaving}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </LoadingButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default ConfigureDataModal;
